import { CardHeader, IconButton } from '@hue/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  formatCurrency,
  Input,
  Modal,
  SelectAllToggle,
  useSnackbar,
} from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Copy,
  Mail,
  Settings,
  Star,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  financialSummaryApi,
  getApiError,
  rootApi,
  shortcutsApi,
  type FinancialSummary as FinancialSummaryData,
} from '../lib/api.js';
import { formatDate } from '../lib/time-zone.js';
import { useCompanyScope } from './company-context.js';
import { findCompanySection, sectionsVisibleTo, type CompanySection } from './company-sections.js';
import { ErrorCard, SummaryStrip } from './data-view.js';
import { useLeaveCompany } from './use-leave-company.js';
import { CreateButton, PageHeader } from './page-title.js';

const ownerSchema = z.object({
  firstName: z.string().trim().min(2, 'Informe o nome.'),
  lastName: z.string().trim().min(2, 'Informe o sobrenome.'),
  email: z.email('Informe um e-mail válido.'),
});
type OwnerForm = z.infer<typeof ownerSchema>;

/// A visão geral da empresa, em `/companies/:companyKey`.
///
/// Ela é a primeira tela de quem entra: mostra o dinheiro da empresa e os atalhos para as telas que
/// a pessoa escolheu ou mais abre. Cada pedaço só aparece para quem alcança o que ele resume — o
/// resumo financeiro sai do mesmo `investment.read` que abre Lançamentos, Aportes e Indicadores.
export function CompanyHome() {
  const { company, isRoot, can, timeZone } = useCompanyScope();
  const { leaving, leave } = useLeaveCompany();
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [ownerRequestError, setOwnerRequestError] = useState<string>();
  const [activationLink, setActivationLink] = useState<string>();
  const [linkCopied, setLinkCopied] = useState(false);
  const ownerForm = useForm<OwnerForm>({
    resolver: zodResolver(ownerSchema),
    defaultValues: { firstName: '', lastName: '', email: '' },
  });

  /// Os atalhos são por pessoa e por empresa, e a chave da consulta diz as duas coisas: sem a
  /// empresa, entrar em outra mostraria os atalhos da anterior até a resposta chegar.
  const shortcuts = useQuery({
    queryKey: ['company', 'shortcuts', company.id],
    queryFn: shortcutsApi.read,
    retry: false,
  });
  const reachable = sectionsVisibleTo(can);
  const favorites = resolveSections(shortcuts.data?.favorites, reachable);
  const frequent = resolveSections(shortcuts.data?.frequent, reachable);

  async function inviteOwner(data: OwnerForm) {
    setOwnerRequestError(undefined);
    try {
      const result = await rootApi.inviteOwner(company.id, data);
      const url = new URL('/activate-owner', window.location.origin);
      url.searchParams.set('token', result.activationToken);
      setActivationLink(url.toString());
    } catch (error) {
      setOwnerRequestError(getApiError(error));
    }
  }

  async function copyActivationLink() {
    if (!activationLink) return;
    try {
      await navigator.clipboard.writeText(activationLink);
      setLinkCopied(true);
    } catch {
      setLinkCopied(false);
    }
  }

  function openOwnerModal() {
    ownerForm.reset();
    setOwnerRequestError(undefined);
    setActivationLink(undefined);
    setLinkCopied(false);
    setOwnerModalOpen(true);
  }

  return (
    <>
      <PageHeader
        trail={<>{company.displayName} / Início</>}
        title="Visão geral"
        description={isRoot ? 'Contexto isolado do tenant ativo.' : 'Visão geral da sua empresa.'}
        actions={
          <>
            {isRoot ? (
              <CreateButton onClick={openOwnerModal}>Cadastrar proprietário</CreateButton>
            ) : null}
          </>
        }
      />

      {/* Antes dos atalhos de propósito: atalho a pessoa fixou e sabe onde está; parcela vencida
          apareceu sozinha, e ninguém entra no sistema para procurá-la. */}
      {can('investment.read') ? (
        <FinancialSummary companyId={company.id} companyKey={company.key} timeZone={timeZone} />
      ) : null}

      <section aria-labelledby="favoritos" className="mb-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2
            id="favoritos"
            className="text-sm font-semibold uppercase tracking-[.12em] text-content-faint"
          >
            Favoritos
          </h2>
          <Button variant="outline" size="sm" onClick={() => setFavoritesOpen(true)}>
            <Star className="size-4" />
            {favorites.length === 0 ? 'Escolher favoritos' : 'Editar favoritos'}
          </Button>
        </div>
        {favorites.length === 0 ? (
          <p className="rounded-xl border border-dashed border-surface-border bg-surface-elevated px-4 py-6 text-center text-sm text-content-muted">
            Nada fixado ainda. Escolha as telas que você abre todo dia e elas ficam aqui.
          </p>
        ) : (
          <ShortcutGrid sections={favorites} companyKey={company.key} />
        )}
      </section>

      {/* Enche sozinha: aparece quando já houver o que contar. Uma seção vazia explicando que vai
          se preencher é ruído no primeiro dia de quem entrou. */}
      {frequent.length > 0 ? (
        <section aria-labelledby="mais-acessados" className="mb-8">
          <h2
            id="mais-acessados"
            className="mb-3 text-sm font-semibold uppercase tracking-[.12em] text-content-faint"
          >
            Mais acessados
          </h2>
          <ShortcutGrid sections={frequent} companyKey={company.key} />
        </section>
      ) : null}

      <Card className="max-w-xl bg-surface-card">
        <CardHeader title="Ambiente ativo" icon={<Building2 className="size-4" />} />
        <div className="flex flex-col items-start gap-4 sm:flex-row">
          <div className="min-w-0">
            <h3 className="mt-2 text-xl font-semibold">{company.displayName}</h3>
            {isRoot ? (
              <p className="mt-1 font-mono text-xs text-content-muted">{company.key}</p>
            ) : null}
            <p className="mt-2 flex items-center gap-2 text-xs text-content-muted">
              <Clock3 className="size-3.5 text-brand-400" />
              {isRoot ? company.timeZone : 'Horário local configurado'}
            </p>
            <p className="mt-4 text-sm leading-6 text-content-secondary">
              {isRoot
                ? 'Tudo o que for feito daqui vale só para esta empresa, e fica registrado na trilha dela.'
                : `Você alcança ${String(reachable.length)} ${reachable.length === 1 ? 'tela' : 'telas'} nesta empresa. O dia a dia fica no menu ao lado; o que administra a empresa, na engrenagem de Configurações.`}
            </p>
          </div>
        </div>
      </Card>

      {isRoot ? (
        <Button
          className="mt-6 lg:hidden"
          variant="outline"
          loading={leaving}
          onClick={() => void leave()}
        >
          <ArrowLeft className="size-4" />
          Voltar ao console global
        </Button>
      ) : null}

      {/* Só existe enquanto está aberto: assim reabrir mostra o que está gravado hoje, e não a
          escolha que alguém abandonou sem salvar da última vez. */}
      {favoritesOpen ? (
        <FavoritesDialog
          onClose={() => setFavoritesOpen(false)}
          companyId={company.id}
          available={reachable}
          chosen={favorites}
        />
      ) : null}

      <Modal
        open={ownerModalOpen}
        onOpenChange={setOwnerModalOpen}
        title={activationLink ? 'Convite enviado' : 'Proprietário do tenant'}
        description={
          activationLink
            ? 'O convite foi enviado por e-mail para a pessoa indicada.'
            : `Defina quem será o primeiro proprietário de ${company.displayName}.`
        }
      >
        {activationLink ? (
          <div>
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-positive/30 bg-positive/10 p-4">
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-positive" />
              <div>
                {/* Token, e não um verde escrito à mão: `emerald-200` some sobre o fundo branco
                    do tema claro, que é onde este painel também aparece. */}
                <h3 className="font-semibold text-positive">Convite a caminho por e-mail</h3>
                <p className="mt-1 text-sm leading-5 text-content-secondary">
                  O link expira em 24 horas e pode ser utilizado uma única vez.
                </p>
              </div>
            </div>
            <Input
              label="Link de ativação"
              value={activationLink}
              readOnly
              hint="Use este link apenas se o e-mail não chegar — entregue-o somente à pessoa convidada."
            />
            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
              <Button
                className="w-full sm:w-auto"
                type="button"
                variant="outline"
                onClick={() => setOwnerModalOpen(false)}
              >
                Fechar
              </Button>
              <Button
                className="w-full sm:w-auto"
                type="button"
                onClick={() => void copyActivationLink()}
              >
                {linkCopied ? <CheckCircle2 className="size-4" /> : <Copy className="size-4" />}
                {linkCopied ? 'Link copiado' : 'Copiar link'}
              </Button>
            </div>
          </div>
        ) : (
          <form noValidate onSubmit={(event) => void ownerForm.handleSubmit(inviteOwner)(event)}>
            <div className="grid gap-5 sm:grid-cols-2">
              <Input
                label="Nome"
                autoComplete="given-name"
                error={ownerForm.formState.errors.firstName?.message}
                {...ownerForm.register('firstName')}
              />
              <Input
                label="Sobrenome"
                autoComplete="family-name"
                error={ownerForm.formState.errors.lastName?.message}
                {...ownerForm.register('lastName')}
              />
              <Input
                className="sm:col-span-2"
                label="E-mail"
                type="email"
                autoComplete="email"
                leftIcon={<Mail className="size-4" />}
                error={ownerForm.formState.errors.email?.message}
                {...ownerForm.register('email')}
              />
            </div>
            <p className="mt-4 text-xs leading-5 text-content-muted">
              O proprietário terá MFA obrigatório e não poderá ser removido enquanto for o último
              owner ativo.
            </p>
            {ownerRequestError ? (
              <div
                role="alert"
                className="mt-5 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
              >
                {ownerRequestError}
              </div>
            ) : null}
            <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
              <Button
                className="w-full sm:w-auto"
                type="button"
                variant="outline"
                onClick={() => setOwnerModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button className="w-full sm:w-auto" loading={ownerForm.formState.isSubmitting}>
                Enviar convite
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

/// O resumo financeiro da empresa — o painel que a tela de início mostra a quem enxerga o
/// investimento.
///
/// Uma chamada só, e não a listagem de lançamentos mais a de aportes: a primeira tela do dia não
/// deve custar duas listas inteiras para exibir quatro números. A soma vem pronta do servidor, que é
/// quem sabe que dia é hoje na empresa — o navegador de quem viaja diria outro.
function FinancialSummary({
  companyId,
  companyKey,
  timeZone,
}: {
  companyId: string;
  companyKey: string;
  timeZone: string;
}) {
  /// Por empresa, como os atalhos: sem isso, entrar em outra mostraria o dinheiro da anterior até a
  /// resposta chegar.
  const summary = useQuery({
    queryKey: ['company', 'financial-summary', companyId],
    queryFn: financialSummaryApi.get,
    retry: false,
  });

  return (
    <section aria-labelledby="resumo-financeiro" className="mb-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2
          id="resumo-financeiro"
          className="text-sm font-semibold uppercase tracking-[.12em] text-content-faint"
        >
          Resumo financeiro
        </h2>
        <Link
          to="/companies/$companyKey/metrics"
          params={{ companyKey }}
          className="flex items-center gap-1 rounded-lg text-sm font-medium text-brand-400 transition hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          Ver indicadores
          <ChevronRight className="size-4" aria-hidden="true" />
        </Link>
      </div>

      {/* Três estados e não quatro: sem resposta e sem erro é carregando, e escrever o quarto caso
          criaria um galho que nenhum teste pode alcançar. */}
      {summary.error ? (
        <ErrorCard message={getApiError(summary.error)} />
      ) : summary.data ? (
        <>
          <SummaryStrip
            label="Resumo financeiro da empresa"
            items={[
              {
                label: 'Aportado',
                value: formatCurrency(summary.data.contributions.contributedCents),
              },
              { label: 'Comprado', value: formatCurrency(summary.data.entries.purchasedCents) },
              {
                label: 'A reembolsar',
                value: formatCurrency(summary.data.entries.outstandingCents),
                tone: 'warning',
              },
            ]}
          />
          <DueLines summary={summary.data} companyKey={companyKey} timeZone={timeZone} />
        </>
      ) : (
        // Do tamanho do que vai entrar: um vazio que cresce depois empurra os atalhos para baixo
        // debaixo do dedo de quem já ia clicar.
        <div
          role="status"
          aria-label="Carregando o resumo financeiro"
          className="h-[66px] animate-pulse rounded-xl border border-surface-border bg-surface-card"
        />
      )}
    </section>
  );
}

/// O que vence, num cartão por urgência, lado a lado como painel e clicável.
///
/// Linha só existe quando há o que dizer: "0 parcelas vencidas" ocupa o mesmo espaço de um aviso de
/// verdade e ensina a ignorar o lugar onde o aviso vai aparecer.
function DueLines({
  summary,
  companyKey,
  timeZone,
}: {
  summary: FinancialSummaryData;
  companyKey: string;
  timeZone: string;
}) {
  const quiet = summary.overdue.count === 0 && summary.dueSoon.count === 0;

  if (quiet) {
    return (
      <p className="flex items-center gap-2 text-sm text-content-muted">
        <CheckCircle2 className="size-4 shrink-0 text-positive" aria-hidden="true" />
        {summary.nextDueOn === null
          ? 'Nenhuma parcela em aberto com vencimento.'
          : `Nada vence nos próximos ${String(summary.horizonDays)} dias. A próxima é ${formatDate(`${summary.nextDueOn}T12:00:00Z`, timeZone)}.`}
      </p>
    );
  }

  return (
    <ul className="grid gap-3 min-[420px]:grid-cols-2 lg:max-w-2xl">
      {summary.overdue.count > 0 ? (
        <li>
          <DueLine
            companyKey={companyKey}
            tone="danger"
            icon={<AlertTriangle className="size-4 shrink-0" aria-hidden="true" />}
            text={`${installmentCount(summary.overdue.count)} ${summary.overdue.count === 1 ? 'vencida' : 'vencidas'} sem reembolso`}
            value={formatCurrency(summary.overdue.cents)}
          />
        </li>
      ) : null}
      {summary.dueSoon.count > 0 ? (
        <li>
          <DueLine
            companyKey={companyKey}
            tone="warning"
            icon={<CalendarClock className="size-4 shrink-0" aria-hidden="true" />}
            text={`${installmentCount(summary.dueSoon.count)} ${summary.dueSoon.count === 1 ? 'vence' : 'vencem'} em até ${String(summary.horizonDays)} dias`}
            value={formatCurrency(summary.dueSoon.cents)}
          />
        </li>
      ) : null}
    </ul>
  );
}

function DueLine({
  companyKey,
  tone,
  icon,
  text,
  value,
}: {
  companyKey: string;
  tone: 'danger' | 'warning';
  icon: ReactNode;
  text: string;
  value: string;
}) {
  return (
    <Link
      to="/companies/$companyKey/entries"
      params={{ companyKey }}
      className={`flex h-full min-h-32 flex-col justify-between gap-4 rounded-xl border p-4 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
        tone === 'danger'
          ? 'border-danger/35 bg-danger/10 text-danger hover:bg-danger/15'
          : 'border-warning/35 bg-warning/10 text-warning hover:bg-warning/15'
      }`}
    >
      <span className="flex items-center justify-between gap-3">
        {icon}
        <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
      </span>
      <span className="grid gap-1">
        <strong className="text-2xl font-semibold tabular-nums">{value}</strong>
        <span className="font-medium">{text}</span>
      </span>
    </Link>
  );
}

function installmentCount(count: number): string {
  return `${String(count)} ${count === 1 ? 'parcela' : 'parcelas'}`;
}

/// As chaves guardadas viram seções de verdade, e só as que esta pessoa alcança.
///
/// O servidor já filtra as duas coisas — chave fora do catálogo e tela fora do acesso. Isto é o
/// cinto por cima do suspensório, e vale o preço: cartão que não abre é pior do que cartão nenhum.
function resolveSections(
  keys: readonly string[] | undefined,
  allowed: readonly CompanySection[],
): CompanySection[] {
  return (keys ?? [])
    .map((key) => findCompanySection(key))
    .filter(
      (section): section is CompanySection =>
        section !== undefined && allowed.some((one) => one.key === section.key),
    );
}

/// Os cartões de acesso rápido. São links, e não botões: abrir Lançamentos numa aba nova é uma
/// coisa que se quer fazer a partir de um atalho.
function ShortcutGrid({
  sections,
  companyKey,
}: {
  sections: readonly CompanySection[];
  companyKey: string;
}) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {sections.map((section) => (
        <li key={section.key}>
          <Link
            to={section.to}
            params={{ companyKey }}
            className="flex min-h-16 w-full items-center gap-3 rounded-xl border border-surface-border bg-surface-card px-4 py-3 text-left transition hover:border-brand-500/50 hover:bg-brand-500/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-400">
              <section.icon className="size-5" aria-hidden="true" />
            </span>
            <SectionName section={section} />
            <ChevronRight className="size-4 shrink-0 text-content-faint" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

/// O nome da tela no atalho. A que mora atrás da engrenagem leva a mesma marca de Configurações:
/// sem ela, "Unidades" num atalho não diz que abre a administração da empresa, e não o dia a dia.
function SectionName({ section }: { section: CompanySection }) {
  return (
    <span className="grid min-w-0 flex-1">
      <span className="truncate text-sm font-medium">{section.label}</span>
      {section.placement === 'settings' ? (
        <span className="flex items-center gap-1 text-xs text-content-muted">
          <span className="sr-only">,</span> <Settings className="size-3" aria-hidden="true" />
          Configurações
        </span>
      ) : null}
    </span>
  );
}

/// Escolher e ordenar os favoritos.
///
/// A ordem se mexe com botões, e não arrastando: arrastar não tem gesto equivalente no teclado e é
/// impreciso no celular de 320 px, que é onde esta tela mais é aberta. Subir e descer funcionam nos
/// três lugares.
function FavoritesDialog({
  onClose,
  companyId,
  available,
  chosen,
}: {
  onClose: () => void;
  companyId: string;
  available: readonly CompanySection[];
  chosen: readonly CompanySection[];
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [keys, setKeys] = useState<string[]>(() => chosen.map((section) => section.key));
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: (screens: readonly string[]) => shortcutsApi.setFavorites(screens),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['company', 'shortcuts', companyId] });
      snackbar.success('Seus favoritos foram atualizados.');
      onClose();
    },
    onError: (requestError: unknown) => setError(getApiError(requestError)),
  });

  function toggle(key: string) {
    setError(undefined);
    setKeys((current) =>
      current.includes(key) ? current.filter((one) => one !== key) : [...current, key],
    );
  }

  function move(key: string, by: -1 | 1) {
    setKeys((current) => {
      const from = current.indexOf(key);
      const to = from + by;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      if (moved) next.splice(to, 0, moved);
      return next;
    });
  }

  const ordered = resolveSections(keys, available);

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Favoritos"
      description="Escolha as telas que você abre todo dia. Elas aparecem no topo do início, na ordem que você deixar."
    >
      <div className="mb-2 flex justify-end">
        <SelectAllToggle
          scope="telas desta empresa"
          total={available.length}
          selected={available.filter((section) => keys.includes(section.key)).length}
          onSelectAll={() => {
            setError(undefined);
            // Quem já estava marcado mantém a ordem; o resto entra no fim.
            setKeys((current) => [
              ...current,
              ...available.map((section) => section.key).filter((key) => !current.includes(key)),
            ]);
          }}
          onClear={() => {
            setError(undefined);
            setKeys([]);
          }}
        />
      </div>
      <ul aria-label="Telas desta empresa" className="grid gap-2">
        {available.map((section) => {
          const position = keys.indexOf(section.key);
          const picked = position >= 0;
          return (
            <li
              key={section.key}
              className="flex items-center gap-2 rounded-xl border border-surface-border px-3 py-2"
            >
              <button
                type="button"
                aria-pressed={picked}
                onClick={() => toggle(section.key)}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-lg ${
                    picked ? 'bg-brand-500/15 text-brand-400' : 'bg-content/5 text-content-faint'
                  }`}
                >
                  <Star className={`size-4 ${picked ? 'fill-current' : ''}`} aria-hidden="true" />
                </span>
                <SectionName section={section} />
              </button>
              {picked ? (
                <span className="flex shrink-0 items-center gap-1">
                  <IconButton
                    label={`Subir ${section.label}`}
                    icon={<ArrowUp />}
                    disabled={position === 0}
                    onClick={() => move(section.key, -1)}
                  />
                  <IconButton
                    label={`Descer ${section.label}`}
                    icon={<ArrowDown />}
                    disabled={position === keys.length - 1}
                    onClick={() => move(section.key, 1)}
                  />
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>

      {ordered.length > 0 ? (
        <p className="mt-4 text-xs leading-5 text-content-muted">
          Nesta ordem: {ordered.map((section) => section.label).join(' · ')}
        </p>
      ) : (
        <p className="mt-4 text-xs leading-5 text-content-muted">
          Sem nenhum favorito, a seção some do início e só "mais acessados" fica.
        </p>
      )}

      {/* Erro de salvar fica inline, ao lado do botão: mensagem que some em cinco segundos não
          serve a quem precisa corrigir e tentar de novo. */}
      {error ? (
        <div
          role="alert"
          className="mt-5 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          {error}
        </div>
      ) : null}

      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={save.isPending}
          onClick={() => save.mutate(keys)}
        >
          Salvar favoritos
        </Button>
      </div>
    </Modal>
  );
}
